import { CalendarOutlined } from '@ant-design/icons';
import { DatePicker, Dropdown, Input, type FormInstance } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  placeholderField,
  setNestedDynamicFields,
} from '@client/utils/form.util';

type TCustomDatePickerProps = {
  disabled?: boolean;
  formRef: FormInstance;
  fieldName: string[];
  label: string;
};

const DATE_FORMAT = 'MMM, DD YYYY';
const DEFAULT_DATE = { key: '30-days', days: 30 };

export const ExpirationDatePicker = (props: TCustomDatePickerProps) => {
  const [visible, setVisible] = React.useState(false);
  const [panelVisible, setPanelVisible] = React.useState(false);

  const { formRef, disabled, fieldName, label } = props;

  const { t } = useTranslation();

  const [activeKey, setActiveKey] = React.useState<string | undefined>(
    DEFAULT_DATE.key,
  );

  const [date, setDate] = React.useState<Dayjs | null>(() => {
    const initialDate = dayjs().add(DEFAULT_DATE.days, 'day');
    setNestedDynamicFields(formRef, fieldName, initialDate);
    return initialDate;
  });

  const handleDateChange = (newDate: Dayjs | null, key?: string) => {
    setDate(newDate);
    setNestedDynamicFields(formRef, fieldName, newDate);
    setActiveKey(key);
    setVisible(false);
  };

  const formatDateLabel = (label: string, date: Dayjs) =>
    `${label} (${date.format(DATE_FORMAT)})`;

  const getDateByDays = (days: number) => dayjs().add(days, 'day');

  const dateOptions = [
    { key: '7-days', days: 7 },
    DEFAULT_DATE,
    { key: '60-days', days: 60 },
    { key: '90-days', days: 90 },
  ];

  return (
    <Dropdown
      arrow
      disabled={disabled}
      open={visible}
      trigger={['click']}
      destroyOnHidden
      onOpenChange={(open) => {
        setVisible(open);
        if (!open) {
          setPanelVisible(false);
        }
      }}
      menu={{
        selectable: true,
        selectedKeys: activeKey ? [activeKey] : [],
        items: [
          {
            key: 'today',
            label: formatDateLabel(t('form.today'), dayjs()),
            onClick() {
              handleDateChange(dayjs(), 'today');
            },
          },
          {
            key: 'tomorrow',
            label: formatDateLabel(t('form.tomorrow'), getDateByDays(1)),
            onClick() {
              handleDateChange(getDateByDays(1), 'tomorrow');
            },
          },
          ...dateOptions.map(({ key, days }) => {
            const date = getDateByDays(days);
            return {
              key,
              label: formatDateLabel(t('form.days', { count: days }), date),
              onClick() {
                handleDateChange(date, key);
              },
            };
          }),
          {
            key: 'custom-date',
            label: (
              <div
                style={{ position: 'relative', overflow: 'hidden' }}
                onClick={(e) => {
                  e.stopPropagation();
                  setPanelVisible(true);
                }}
              >
                <div>{t('form.customize')}</div>
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <DatePicker
                    open={panelVisible}
                    format={DATE_FORMAT}
                    styles={{
                      root: {
                        pointerEvents: 'none',
                        opacity: 0,
                        position: 'absolute',
                        bottom: -12,
                        insetInlineStart: 0,
                      },
                    }}
                    onChange={(date) => {
                      handleDateChange(date, 'custom-date');
                      setPanelVisible(false);
                    }}
                  />
                </div>
              </div>
            ),
          },
          {
            key: 'no-expiration',
            label: t('form.noExpiration'),
            onClick() {
              handleDateChange(null, 'no-expiration');
            },
          },
        ],
      }}
    >
      <Input
        disabled={disabled}
        value={date ? date.format(DATE_FORMAT) : t('form.noExpiration')}
        placeholder={placeholderField(label, 'select')}
        suffix={<CalendarOutlined />}
      />
    </Dropdown>
  );
};
